import React, { useMemo } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { ArrowRight, KeyRound, ShieldCheck, History, CalendarDays, Users, AlertTriangle } from 'lucide-react';
import PageHeader from './PageHeader';

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('pl-PL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('pl-PL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function toneClass(tone) {
  if (tone === 'success') return 'success';
  if (tone === 'warning') return 'warning';
  if (tone === 'danger') return 'danger';
  return 'info';
}

function accessLabel(event) {
  if (!event) return '—';
  const parts = [event.auth_name || '—'];
  if (event.action !== null && event.action !== undefined) parts.push(`akcja ${event.action}`);
  if (event.source !== null && event.source !== undefined) parts.push(`źródło ${event.source}`);
  return parts.join(' · ');
}

export default function Nuki() {
  const navigate = useNavigate();
  const ctx = useOutletContext() || {};
  const nuki = ctx.dashboard?.nuki_status || {};

  const codeStats = nuki.code_stats || { total: 0, planned: 0, active: 0, error: 0, deleted: 0 };
  const recentAccess = useMemo(() => Array.isArray(nuki.recent_access_events) ? nuki.recent_access_events : [], [nuki.recent_access_events]);
  const upcomingCodes = useMemo(() => Array.isArray(nuki.upcoming_codes) ? nuki.upcoming_codes : [], [nuki.upcoming_codes]);

  const battery = nuki.battery || {};
  const batteryTone = battery.available
    ? (battery.critical || (battery.percent !== null && battery.percent !== undefined && battery.percent < 20)
      ? 'danger'
      : (battery.percent !== null && battery.percent !== undefined && battery.percent < 50 ? 'warning' : 'success'))
    : 'warning';

  const mariaLast = nuki.last_maria_event;
  const guestLast = nuki.last_guest_event || nuki.last_guest_2_event;
  const accessLast = nuki.last_access_event;
  const batteryPercent = Number.isFinite(Number(battery.percent)) ? Number(battery.percent) : null;
  const batteryFill = batteryPercent !== null ? Math.max(0, Math.min(100, batteryPercent)) : 0;

  const nextBookingName = upcomingCodes[0]?.guest_name || '—';
  const nextBookingDate = upcomingCodes[0]?.allowed_from || null;

  return (
    <div className="page-content page-nuki">
      <PageHeader
        icon="🔐"
        title="Nuki"
        description="Kokpit zamka: status synchronizacji, ostatnia Maria, ostatni gość i zalecany sposób użycia."
        helpKey="nuki"
        stats={[
          { label: 'Status', value: nuki.short_status || '—', tone: toneClass(nuki.tone) },
          { label: 'Kody', value: codeStats.total ?? 0, tone: 'info' },
          { label: 'Aktywne', value: codeStats.active ?? 0, tone: 'success' },
          { label: 'Bateria', value: battery.percent !== null && battery.percent !== undefined ? `${battery.percent}%` : (battery.label || 'Brak danych'), tone: batteryTone },
          { label: 'Maria ostatnio', value: mariaLast ? formatDateTime(mariaLast.event_date) : '—', tone: mariaLast ? 'info' : 'warning' },
          { label: 'Gość ostatnio', value: guestLast ? formatDateTime(guestLast.event_date) : '—', tone: guestLast ? 'info' : 'warning' },
        ]}
        actions={(
          <button type="button" className="primary" onClick={() => navigate('/rezerwacje')}>
            <ArrowRight size={16} /> Do rezerwacji
          </button>
        )}
        helpContext={{
          label: nuki.label,
          codeStats,
          mariaLast,
          guestLast,
        }}
      />

      <section className="dashboard-grid dashboard-grid-optimized">
        <article className={`card status-card ${toneClass(nuki.tone)}`}>
          <div className="status-card-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span className={`status-chip ${toneClass(nuki.tone)}`}>Status Nuki</span>
            <span className={`status-chip ${batteryTone}`}>Poziom baterii {batteryPercent !== null ? `${batteryPercent}%` : '—'}</span>
          </div>
          <h2>{nuki.label || 'Brak danych Nuki'}</h2>
          <p className="muted">Ostatni przebieg: {nuki.latest_run ? formatDateTime(nuki.latest_run.finished_at || nuki.latest_run.started_at) : '—'}</p>
          {nuki.latest_run && (
            <p className="muted">Run: {nuki.latest_run.status}{nuki.latest_run.error ? ` · ${nuki.latest_run.error}` : ''}</p>
          )}

          <div className="maria-prep-box" style={{ marginTop: 12, marginBottom: 12 }}>
            <strong>Bateria zamka</strong>
            <span>
              To jest procent naładowania baterii samej Nuki — nie telefonu i nie ogólny status systemu.
              Gdy odczyt jest dostępny, pasek poniżej pokazuje dokładny poziom.
            </span>
          </div>

          <div className="settlement-breakdown">
            <p className="muted" style={{ margin: '0 0 6px 0' }}>
              Bateria zamka — procent naładowania samej Nuki, nie telefonu.
            </p>
            <p className="muted" style={{ margin: '0 0 6px 0', fontWeight: 700 }}>
              {batteryPercent !== null ? `${batteryPercent}% naładowania` : battery.label || 'Brak danych'}
              {battery.critical === true ? ' · krytyczna' : battery.critical === false ? ' · niekrytyczna' : ''}
            </p>
            <div
              className="battery-meter"
              role="progressbar"
              aria-label="Poziom naładowania baterii Nuki"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={batteryPercent !== null ? batteryPercent : undefined}
              aria-valuetext={batteryPercent !== null ? `${batteryPercent} procent naładowania baterii` : 'Brak danych o baterii'}
              style={{
                marginTop: 10,
                height: 12,
                borderRadius: 999,
                overflow: 'hidden',
                background: '#e5e7eb',
                border: '1px solid #d1d5db',
              }}
            >
              <span
                style={{
                  display: 'block',
                  height: '100%',
                  width: `${batteryFill}%`,
                  background: batteryTone === 'danger' ? '#ef4444' : batteryTone === 'warning' ? '#f59e0b' : '#22c55e',
                  transition: 'width 0.2s ease',
                }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
              {battery.updated_at ? <small className="muted">Aktualizacja {formatDateTime(battery.updated_at)}</small> : <small className="muted"> </small>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
              <small className="muted">0%</small>
              <small className="muted">100%</small>
            </div>
            <p className="muted">Najbliższy kod: {nextBookingName} · {nextBookingDate ? formatDateTime(nextBookingDate) : '—'}</p>
            {battery.fallback && (
              <p className="muted strong-muted">
                Fallback: {battery.fallback_note || 'użyto zastępczego odczytu, bo główne pobranie szczegółów nie odpowiedziało.'}
              </p>
            )}
            <p className="muted">Kody lokalne: {codeStats.total ?? 0} · planowane: {codeStats.planned ?? 0} · aktywne: {codeStats.active ?? 0} · błędy: {codeStats.error ?? 0}</p>
          </div>

          <div className="settlement-actions" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="secondary" onClick={() => navigate('/sprzatanie')}>
              <ShieldCheck size={16} /> Sprzątanie
            </button>
            <button type="button" className="secondary" onClick={() => navigate('/dashboard')}>
              <History size={16} /> Dashboard
            </button>
          </div>
        </article>

        <article className="card status-card info">
          <div className="status-card-head">
            <span className="status-chip info">Ostatnio Maria / gość</span>
          </div>
          <h2>Historia użycia klucza</h2>
          <div className="settlement-breakdown">
            <p className="muted strong-muted">
              Maria ostatnio: {mariaLast ? `${formatDateTime(mariaLast.event_date)} · ${accessLabel(mariaLast)}` : 'Brak zapisanego użycia przez Marię'}
            </p>
            <p className="muted strong-muted">
              Gość ostatnio: {guestLast ? `${formatDateTime(guestLast.event_date)} · ${accessLabel(guestLast)}` : 'Brak zapisanego użycia przez gościa'}
            </p>
            {accessLast && (
              <p className="muted">Ostatni dostęp ogólnie: {formatDateTime(accessLast.event_date)} · {accessLabel(accessLast)}</p>
            )}
          </div>
          <div className="maria-prep-box" style={{ marginTop: 12 }}>
            <strong>Interpretacja</strong>
            <span>Maria ma mieć osobny, stały kod roboczy, a gość ma dostać kod tylko na swój pobyt. To rozdziela sprzątanie od pobytu i usuwa ryzyko pomyłki w czasie.</span>
          </div>
        </article>

        <article className="card status-card success">
          <div className="status-card-head">
            <span className="status-chip success">Najlepsze wykorzystanie</span>
          </div>
          <h2>Jak używać Nuki najlepiej</h2>
          <ul className="bulleted-list">
            <li><strong>Kod gościa</strong> generuj tylko dla aktywnej rezerwacji i wygaszaj po checkout.</li>
            <li><strong>Kod Marii</strong> trzymaj jako osobny, stały dostęp roboczy do sprzątania.</li>
            <li><strong>Kod awaryjny właściciela</strong> używaj do sytuacji wyjątkowych i audytu.</li>
            <li><strong>Ta strona</strong> ma być codziennym panelem kontroli: status, ostatnie wejścia, najbliższy kod, błędy synchronizacji.</li>
            <li><strong>Operacyjnie</strong>: jeśli Maria lub gość nie użyli kodu wtedy, gdy powinni, to od razu widać to tutaj — bez szukania w kilku miejscach.</li>
          </ul>
        </article>

        <article className="card status-card warning">
          <div className="status-card-head">
            <span className="status-chip warning">Ostatnie zdarzenia</span>
          </div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Kto</th>
                  <th>Kiedy</th>
                  <th>Info</th>
                </tr>
              </thead>
              <tbody>
                {recentAccess.length > 0 ? recentAccess.map((ev, idx) => (
                  <tr key={`${ev.event_date}-${idx}`}>
                    <td>{ev.auth_name || '—'}</td>
                    <td>{formatDateTime(ev.event_date)}</td>
                    <td>{ev.action !== null && ev.action !== undefined ? `akcja ${ev.action}` : '—'}{ev.source !== null && ev.source !== undefined ? ` · źródło ${ev.source}` : ''}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={3} className="empty-cell">Brak danych o ostatnich zdarzeniach Nuki.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </article>

        <article className="card status-card info">
          <div className="status-card-head">
            <span className="status-chip info">Najbliższe kody</span>
          </div>
          <h2>Planned / active</h2>
          <div className="settlement-breakdown">
            {upcomingCodes.length > 0 ? upcomingCodes.map(code => (
              <div key={`${code.booking_id}-${code.allowed_from}`} className="maria-prep-box" style={{ marginBottom: 10 }}>
                <strong>{code.guest_name || 'Gość'} · {code.status}</strong>
                <span>Od: {formatDateTime(code.allowed_from)} · Do: {formatDateTime(code.allowed_until)}</span>
                {code.last_error && <small><AlertTriangle size={14} /> {code.last_error}</small>}
              </div>
            )) : (
              <p className="muted">Brak zaplanowanych lub aktywnych kodów do pokazania.</p>
            )}
          </div>
        </article>
      </section>
    </div>
  );
}
